// Must precede every other import: a chunk's CSS is emitted in module-execution order and
// equal-specificity rules are won by the last one, so the globals have to land first to stay
// the base layer that component styles override.
import "./index.scss";
import { Agent } from "@fingerprintjs/fingerprintjs";
import { Agent as AgentPro } from "@fingerprintjs/fingerprintjs-pro";
import axios from "axios";
import { Locale as ContentfulLocale } from "contentful";
import Cookies from "js-cookie";
import { ACTIVE_LOCALE_COOKIE, addScript, addStylesheet, LocaleStatus } from "./bootstrap-utils";
import { setBetbyScriptUrl } from "./sports/providers/betby-script-loader";
import ContentfulClientService from "./contenful/ContentfulClientService";
import StrapiClient, { StrapiLocale } from "./contenful/StrapiClient";
import { Locale } from "./contenful/types";
import { HttpService } from "./http/HttpService";
import { JwtStore } from "./http/JwtStore";
import { RefreshCoordinator } from "./http/RefreshCoordinator";
import { Config } from "./http/Types";
import { getActiveLocale, getLocaleFromPath, setupI18n } from "./i18n";
import * as serviceWorker from "./serviceWorker";
import { SettingsService, SettingsWithSite } from "./settings/SettingsService";
import { FeatureFlagsService } from "./core/feature-flags/FeatureFlagService";
import { FliptService } from "./core/feature-flags/FliptService";
import { FLIPT_NAMESPACE } from "./core/feature-flags/FliptFlagConfig";
import { initCloudWatchRum } from "./core/monitoring/CloudWatchRum";
import { loadRuntimeRouteConfig, setRuntimeRoutes } from "./routing/runtime/runtimeRouteSource";

if ("serviceWorker" in navigator) {
  serviceWorker.register({
    onUpdate: (registration) => {
      if (registration.waiting) {
        registration.waiting.postMessage({ type: "SKIP_WAITING" });
      }
    },
  });
}

declare global {
  interface Window {
    deferredPrompt: Event;
    installedUsingNativePrompt: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    PasswordCredential: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    FederatedCredential: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    PublicKeyCredential: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    fcWidget: any;
    fingerprint: Agent;
    fingerprintPro: AgentPro;
    GLOBAL_LOADER_IMAGE: string;
    GLOBAL_LOADER_WIDTH: string;
    GLOBAL_METAMAP_SCRIPT_URL: string;
    GLOBAL_FLIPT_URL: string;
    GLOBAL_FLIPT_ENVIRONMENT: string;
    prerenderReady?: boolean;
  }
}

window.addEventListener("beforeinstallprompt", (e) => {
  e.preventDefault();
  window.deferredPrompt = e;
});

initCloudWatchRum();

/**
 * Basic loading process.
 *
 * Index.html
 *   A. load additional head links/preloads
 *   A. load initial js chunk
 *      B. load settings
 *          C. load locales -> load translations
 *          C. load css override
 *          C. load react root chunks/css
 */
(async function () {
  let config: Config | undefined = undefined;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const domain = (window.location as any).hostname.replace(/^www\./, "");

  if (window.localStorage.getItem("config.json")) {
    config = JSON.parse(window.localStorage.getItem("config.json") ?? "") ?? undefined;
  } else if (
    domain == "localhost" ||
    domain == "nano" ||
    domain == "nano-2" ||
    domain == "host.docker.internal" ||
    domain?.startsWith("127.0") ||
    domain?.startsWith("10.0") ||
    domain?.startsWith("192.168")
  ) {
    try {
      config = await getConfig();
      // If accessing via host.docker.internal, replace localhost in baseUrl
      if (domain === "host.docker.internal" && config?.baseUrl?.includes("localhost")) {
        config.baseUrl = config.baseUrl.replace(/localhost/g, "host.docker.internal");
      }
    } catch (e) {
      console.error("[bootstrap] public/config/config.json is missing.", e);
      const hostname = document.location.hostname;
      if (hostname.includes("host.docker.internal")) {
        config = { baseUrl: "http://host.docker.internal:9081/rest" };
      } else if (hostname.startsWith("10.0") || hostname.startsWith("192.168")) {
        config = { baseUrl: "http://" + hostname + ":8081/rest" };
      } else {
        config = { baseUrl: "http://localhost:8081/rest" };
      }
    }
  }
  if (!config) {
    config = { baseUrl: "https://" + domain + "/rest" };
  }

  // Final check: if accessing via host.docker.internal, replace localhost in baseUrl
  if (domain === "host.docker.internal" && config?.baseUrl?.includes("localhost")) {
    config.baseUrl = config.baseUrl.replace(/localhost/g, "host.docker.internal");
  }

  // NS-1864: This bootstrap JwtStore is intentionally separate from the
  // container's JwtStore (see wireApplication in IOCSetup.ts). It only powers
  // pre-wireApplication HTTP calls (/settings, /maintenance, tracking cookies),
  // all of which are unauthenticated. Login flows write to
  // getContainer().getJwtStore(), NOT this instance — so do NOT add any
  // authenticated endpoint to a service bound to this bootstrap HttpService.
  const bootstrapJwtStore = new JwtStore();
  const httpService = new HttpService(config.baseUrl, bootstrapJwtStore, new RefreshCoordinator(config.baseUrl, bootstrapJwtStore));
  const featureFlagsService = FeatureFlagsService.getInstance();
  const settingsService = SettingsService.getInstance(httpService);
  // Started here, before the settings request it depends on nothing from: awaiting it
  // further down would put a serial round trip in front of the largest chunk's download on
  // every boot, for every operator. It never rejects, so the maintenance-mode return below
  // cannot leave an unhandled rejection behind.
  const runtimeRouteDeclaration = loadRuntimeRouteConfig();
  let settings: SettingsWithSite;
  try {
    settings = await settingsService.getSettings();
  } catch (e) {
    if (
      e.response?.status == 503 &&
      (e.response?.headers?.["x-maintenance-reason"] ?? "") == "scheduled" &&
      !document.location.pathname.endsWith("/maintenance")
    ) {
      document.location.pathname = "/maintenance";
    } else if (document.location.pathname == "/maintenance") {
      import("./maintenance-on-load").then((r) => {
        r.maintenanceOnLoad(httpService);
      });
    }
    return;
  }

  const finalStrapiUrl = getFinalStrapiUrl(config, settings);

  let fonts = false;
  if (settings && settings.customScripts && settings.customScripts.length > 0) {
    try {
      const scripts = settings.customScripts.split("\n");

      if (scripts.length > 0) {
        scripts.forEach((url: string) => {
          url = url.trim();
          if (url.startsWith("font:")) {
            fonts = true;
            addStylesheet(url.replace("font:", "").trim());
          } else if (url.startsWith("css:")) {
            addStylesheet(url.replace("css:", "").trim());
          } else if (url.startsWith("appModeLogo:")) {
            url = url.replace("appModeLogo:", "");
            settingsService.setAppModeLogo(url);
          } else if (url.startsWith("appModeTitle:")) {
            url = url.replace("appModeTitle:", "");
            settingsService.setAppModeTitle(url);
          } else if (url.startsWith("featureFlags:")) {
            // @deprecated Legacy CSV feature flags from operator custom scripts; flags are Flipt-backed now.
            // Recognised as a no-op so a stale line is dropped rather than falling through to addScript,
            // which would inject a <script> with an invalid URL scheme and error on every bootstrap.
          } else {
            if (url.startsWith("script:")) {
              url = url.replace("script:", "");
            }
            if (url.includes("bt-renderer")) {
              setBetbyScriptUrl(url);
            } else {
              addScript(url);
            }
          }
        });
      }
      //include default fonts
    } catch (e) {
      e;
    }
  }

  // Fire off Flipt init early (non-blocking). The service is registered on the singleton immediately
  // so the provider can drive readiness and live refresh; a slow init is adopted rather than discarded.
  const fliptUrl = getFliptUrl(config);
  const fliptEnvironment = getFliptEnvironment(config);
  if (fliptUrl && fliptEnvironment) {
    const fliptService = new FliptService();
    fliptService.init(fliptUrl, fliptEnvironment, FLIPT_NAMESPACE);
    featureFlagsService.setFliptService(fliptService);
  } else if (fliptUrl || fliptEnvironment) {
    const missing = [!fliptUrl && "fliptUrl", !fliptEnvironment && "fliptEnvironment"].filter(Boolean).join(" and ");
    // eslint-disable-next-line no-console
    console.error(`[FeatureFlags] ${missing} is missing. Both fliptUrl and fliptEnvironment are required. Flipt will not be initialized.`);
  }

  //default fonts
  if (fonts == false) {
    addStylesheet("https://fonts.googleapis.com/css2?family=Asap:wght@400;500;600&display=swap");
    addStylesheet("https://fonts.googleapis.com/css?family=Quantico&display=swap");
  }

  const spaceId = settings.contenfulSpaceId ? settings.contenfulSpaceId : "";
  const accessToken = settings.contentfulAccessToken ? settings.contentfulAccessToken : "";
  const contentfulClientService = new ContentfulClientService(spaceId, accessToken, false);
  const strapiClient = new StrapiClient(finalStrapiUrl);

  if (!config) {
    throw new Error("No config found");
  }

  const promises = Promise.all([
    settings?.cssOverrideUrl ? fetchCssOverride(settings.cssOverrideUrl) : Promise.resolve(""),
    setupLocales(config.baseUrl, settingsService, contentfulClientService, strapiClient),
    settings?.menu && settings?.menu?.settings && settings?.menu?.settings?.iconPack
      ? fetchCssOverride(settings?.menu?.settings?.iconPack)
      : Promise.resolve(""),
  ]);

  // App.tsx computes its route table once, when it is first evaluated, and ./load is what first
  // imports it — so the declaration has to be stored before this import. Stored after, the table
  // is already built and a configured address is silently absent rather than wrong.
  try {
    setRuntimeRoutes(await runtimeRouteDeclaration);
  } catch (error) {
    console.error("[bootstrap] Could not resolve the runtime route declaration.", error);
  }

  import("./load").then((fn) => {
    // eslint-disable-next-line
    // @ts-ignore
    fn.startApplication(config, settings, settingsService, httpService, domain, contentfulClientService, strapiClient, promises, finalStrapiUrl);
  });
})();

async function setupLocales(
  baseUrl: string,
  settingsService: SettingsService,
  contentfulClientService: ContentfulClientService,
  strapiClient: StrapiClient,
): Promise<LocaleStatus> {
  const localeFromPath = getLocaleFromPath();

  const cookieLocale = Cookies.get(ACTIVE_LOCALE_COOKIE);

  let activeLocale;
  if (strapiClient.isEnabled()) {
    const localesList: StrapiLocale[] = await strapiClient.fetchLocales();

    if (localesList && localesList.length > 1) {
      const locales: Array<Locale> = [];
      localesList.map((item: StrapiLocale) => locales.push({ code: item.code, name: item.name, defaultLocale: item.isDefault, fallback: "en" }));
      settingsService.setLocalesData(locales);
    }
    activeLocale = getActiveLocale(settingsService, localeFromPath, cookieLocale);
  } else {
    const localesList = await contentfulClientService.getLocales();
    if (localesList && localesList.items && localesList.items.length > 1) {
      const locales: Array<Locale> = [];
      localesList.items.map((item: ContentfulLocale) =>
        locales.push({ code: item.code, name: item.name, defaultLocale: item.default, fallback: item.fallbackCode }),
      );
      settingsService.setLocalesData(locales);
    }
    activeLocale = getActiveLocale(settingsService, localeFromPath, cookieLocale);
  }

  await setupI18n(baseUrl, activeLocale.code, settingsService);
  return {
    activeLocale: activeLocale,
    localeFromPath: localeFromPath,
  };
}
/**
 * Determines the final Strapi URL to use, applying override from environment variable, config.json, or settings.
 * Precedence: NANO_PLAYER_FRONTEND_STRAPI_URL (env) > config.overrideStrapiUrl > settings.strapiApiUrl
 *
 * @param config - Configuration object that may contain overrideStrapiUrl
 * @param settings - Settings object containing the default strapiApiUrl
 * @returns The final Strapi URL to use
 */
function getFinalStrapiUrl(config: Config, settings: SettingsWithSite): string | undefined {
  // Highest precedence: Environment variable (for local development)
  const envStrapiUrl = import.meta.env.NANO_PLAYER_FRONTEND_STRAPI_URL;
  if (envStrapiUrl) {
    return envStrapiUrl;
  }

  // Second precedence: Config override
  const defaultStrapiUrl = settings.strapiApiUrl;
  const isOverridden = config.overrideStrapiUrl !== undefined && config.overrideStrapiUrl !== null && config.overrideStrapiUrl !== "";

  return isOverridden ? config.overrideStrapiUrl : defaultStrapiUrl;
}

async function getConfig(): Promise<Config> {
  const http = axios.create({
    headers: {
      "Content-type": "application/json",
    },
  });
  return http.get("/config/config.json").then((r) => r.data);
}

export function fetchCssOverride(cssOverrideUrl: string) {
  // Highest precedence: Environment variable (for local development)
  const envCssUrl = import.meta.env.NANO_PLAYER_FRONTEND_CSS_URL;
  if (envCssUrl) {
    cssOverrideUrl = envCssUrl;
  } else {
    // Second precedence: localStorage override (for dev)
    try {
      const devCssOverride = localStorage.getItem("css_override");
      if (devCssOverride !== null) {
        cssOverrideUrl = devCssOverride;
      }
    } catch (e) {
      e;
    }
  }
  return new Promise<string>((resolve, reject) => {
    const link = document.createElement("link");
    link.type = "text/css";
    link.rel = "stylesheet";
    link.onload = () => {
      resolve(cssOverrideUrl);
    };
    link.onerror = () => {
      reject("error loading css");
    };
    link.href = cssOverrideUrl;
    const body = document.querySelector("body");
    body && body.appendChild(link);
  });
}

function getFliptUrl(config: Config): string | undefined {
  return import.meta.env.NANO_PLAYER_FRONTEND_FLIPT_URL ?? (window.GLOBAL_FLIPT_URL || undefined) ?? config.fliptUrl;
}

function getFliptEnvironment(config: Config): string | undefined {
  return import.meta.env.NANO_PLAYER_FRONTEND_FLIPT_ENVIRONMENT ?? (window.GLOBAL_FLIPT_ENVIRONMENT || undefined) ?? config.fliptEnvironment;
}

// Disabled flag in all
export const auditEnabledByFlag = () => {
  /*     const currentDomain = window.location.hostname;
    const flagDomains = [/__blank/];
    const devEnabled = localStorage.getItem("auditflag") === "true"

    for (const regex of flagDomains) {
        if (regex.test(currentDomain) || devEnabled) {
            return true;
        }
    } */

  return localStorage.getItem("auditflag") === "true";
};
